import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { TruncatePipe } from '../../pipes/truncate/truncate.pipe';
import { LoaderComponent } from '../loader/loader.component';
import { ClickOutSideDirective } from '../../directives/clickOutSide.directive';
import { SearchMinLength } from '../../../core/common/enums/search-min-length';
import { OptionItem } from '../option.interface';
import { AvatarComponent } from '../avatar/avatar.component';
import { SearchIconComponent } from '@kuiper/icons';
import { ChevronDownIconComponent } from '@kuiper/icons';

interface UpdataDataBody {
  page: number;
  search: string;
}

@Component({
  selector: 'lib-user-dropdown-input',
  imports: [
    ReactiveFormsModule,
    ClickOutSideDirective,
    TruncatePipe,
    LoaderComponent,
    FormsModule,
    AvatarComponent,
    SearchIconComponent,
    ChevronDownIconComponent,
  ],
  templateUrl: './user-dropdown-input.component.html',
  styleUrl: './user-dropdown-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserDropdownInputComponent),
      multi: true,
    },
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserDropdownInputComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  searchInput = viewChild<ElementRef>('search');

  inputFormControlName = input.required<string>();
  id = input.required<string>();
  name = input.required<string>();

  label = input<string>();
  placeholder = input<string>('');
  inputClass = input<string>('');

  disabled = input<boolean>(false);
  readonly = input<boolean>();
  value = input<string | number>();
  required = input<boolean>(false);

  options = input<OptionItem[] | null>([]);
  selectedKeyValue = input<'value' | 'label'>('value');
  selectedKeyName = input<'value' | 'label'>('label');
  selectedValue = input<OptionItem | null>(null);

  searchLabel = input<string>('Find Users');
  searchPlaceholder = input<string>('Search by name'); //to be able to translate or edit it
  noDataLabel = input<string>('No data'); //to be able to translate or edit it

  changeOption = output<OptionItem | null>();
  updateData = output<UpdataDataBody>();
  isDropdownOpen = false;
  selectedOption: OptionItem = <OptionItem>{};
  focused?: boolean;
  notEmpty?: boolean;
  showNoSearchResult!: boolean;
  filteredOptions: OptionItem[] | undefined = [];

  isLoading = input<boolean>(false);
  page = input<number>(1);
  pageSize = input<number>(15);
  totalPages = input<number>(1);

  searchTerm = signal<string>('');
  searchTerm$ = new Subject<string>();
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);

  avatarSize = model<'list' | 'card' | 'profile' | ''>('');
  imgDimensions = model<{ width: string | number; height: string | number }>({
    width: 32,
    height: 32,
  });
  private lastEmittedOption: OptionItem | null = null;

  private destroy$ = new Subject<void>();
  private parentContainer = inject(ControlContainer);

  private get parentFormGroup(): FormGroup {
    return this.parentContainer.control as FormGroup;
  }

  protected get childControl(): FormControl {
    return this.parentFormGroup?.controls[
      this.inputFormControlName()
    ] as FormControl;
  }

  constructor() {
    effect(() => {
      if (this.selectedValue()) {
        this.selectOption(this.selectedValue());
      }
    });
  }

  ngOnInit(): void {
    if (this.disabled()) {
      this.childControl.disable();
    }
  }

  toggleDropdown() {
    this.filteredOptions = [];
    this.isDropdownOpen = !this.isDropdownOpen;
    this.childControl.markAsTouched();
    if (
      (!this.childControl.value || this.childControl.value === 0) &&
      !this.isDropdownOpen
    ) {
      this.selectOption(null);
    }

    if (this.isDropdownOpen) {
      this.updateData.emit({ page: 1, search: '' });
    }
  }

  ngAfterViewInit(): void {
    this.searchTerm$.pipe(debounceTime(600)).subscribe(term => {
      this.onDropdownSearch(term);
    });
  }

  onSearchTermChange(target: EventTarget | null) {
    const term = (target as HTMLInputElement).value;
    this.searchTerm$.next(term);
  }

  selectOption(item: OptionItem | null, updateFormControlValue?: boolean) {
    if (this.lastEmittedOption === item && item) {
      return; // Prevent duplicate emissions
    }
    this.lastEmittedOption = item;

    if (item) {
      this.selectedOption = item;
      if (updateFormControlValue) {
        this.childControl.patchValue(item[this.selectedKeyValue()]);
      }
      this.filteredOptions = [];
      this.changeOption.emit(item);
      this.notEmpty = true;
    } else {
      this.childControl.markAsDirty();
      this.changeOption.emit(null);
    }
  }

  onDropdownSearch(term: string): void {
    if (term.length >= 3) {
      this.isMoreThanThreeCharacters.set(true);
      const body = {
        page: 1,
        search: term,
      };
      this.searchTerm.set(term);
      this.updateData.emit(body);
    } else if (
      this.isMoreThanThreeCharacters() &&
      term.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      const body = {
        page: 1,
        search: '',
      };
      this.searchTerm.set('');
      this.updateData.emit(body);
    }
  }

  clickedOutside() {
    // set value to null if user didn't select owner
    if (
      this.isDropdownOpen &&
      (!this.childControl.value || this.childControl.value === 0)
    ) {
      this.selectOption(null);
    }

    this.isDropdownOpen = false;

    if (
      this.childControl?.touched &&
      !this.childControl?.value &&
      this.childControl?.hasError('required')
    ) {
      this.childControl.markAsDirty();
    }
  }

  onScroll(event: Event) {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as unknown as {
        scrollTop: number;
        scrollHeight: number;
        clientHeight: number;
      };
    if (
      scrollTop + clientHeight >= scrollHeight - 200 &&
      !this.isLoading() &&
      this.page() < this.totalPages()
    ) {
      const body = {
        page: this.page() + 1,
        search: this.searchTerm(),
      };
      this.updateData.emit(body);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
